import { Injectable } from '@angular/core';
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  getMetadata,
  listAll,
  deleteObject,
  StorageReference,
} from 'firebase/storage';
import { getApp } from 'firebase/app';
import { AuthenticationService } from 'ngx-core-services';
import { first } from 'rxjs/operators';
import { getCountryNameFromCodeV3 } from 'ngx-modbus';

import { SgpInteraction, SgpMetadataFirmwareType, parseSafetyCustomHeader, buildSafetyOverrideKey } from '../../SGP';

// Logger types (from ngx-modbus)
export type LoggerType = 'ZSM-WIFI-USB' | 'ZSM-WIFI-EXT' | 'ZSM-ETH-USB' | 'ZSM-ETH-EXT';

/**
 * Metadata structure for SAFETY binary test firmware
 */
export interface SafetyBinaryMetadata {
  deviceModel: string;        // Device model (e.g., "ZH3", "ZM2")
  buildDate: string;          // Extracted from binary (offset 109-121)
  firmwareName: string;       // Extracted from binary
  firmwareVersion: string;    // Extracted from binary
  uploadedBy: string;
  uploadedAt: string;
  notes?: string;
}

/**
 * Metadata structure for SAFETY custom .txt files
 */
export interface SafetyCustomMetadata {
  deviceModel: string;        // Device model (e.g., "ZH3", "ZM2")
  countryCode: number;        // Parsed from first line
  regionCode: number;         // Parsed from first line
  version: number;            // Parsed from first line
  countryName: string;        // Lookup from code
  overrideKey: string;        // "{countryCode}_{regionCode}_{version}"
  uploadedBy: string;
  uploadedAt: string;
  notes?: string;
}

/**
 * Safety binary item
 */
export interface SafetyBinaryItem {
  path: string;
  name: string;
  size: number;
  uploadedAt: string;
  downloadURL: string;
  metadata: SafetyBinaryMetadata;
}

/**
 * Safety custom .txt item
 */
export interface SafetyCustomItem {
  path: string;
  name: string;
  size: number;
  uploadedAt: string;
  downloadURL: string;
  metadata: SafetyCustomMetadata;
}

/**
 * Metadata structure for INVERTER test firmware
 */
export interface InverterFirmwareMetadata {
  deviceModel: string;      // Device model (e.g., "ZH3", "ZP1", "ZP2")
  moduleType: string;       // Module type (e.g., "ARM", "DSPM", "BATTERY.BMS")
  chipId: string;           // Chip ID (e.g., "G0", "N1", "T1")
  version: string;          // Firmware version (e.g., "V050005")
  notes?: string;           // Optional notes
}

/**
 * Metadata structure for LOGGER test firmware
 */
export interface LoggerFirmwareMetadata {
  deviceType: 'LOGGER';
  loggerType: LoggerType;
  originalFilename: string;
  uploadedBy: string;
  uploadedAt: string;
  notes?: string;
}

/**
 * Test firmware item with full metadata (INVERTER)
 */
export interface InverterFirmwareItem {
  path: string;
  name: string;
  size: number;
  uploadedAt: string;
  downloadURL: string;
  metadata: {
    // From form
    deviceModel: string;
    moduleType: string;
    chipId: string;
    version: string;
    notes: string;

    // From ExtractMetadataFromFirmware
    firmwareName: string;
    firmwareVersion: string;
    firmwareCRC32Hex: string;
    firmwareSizeHex: string;
    chipModel: string;

    // System
    uploadedBy: string;
    uploadedAt: string;
  };
}

/**
 * Logger firmware item
 */
export interface LoggerFirmwareItem {
  path: string;
  name: string;
  size: number;
  uploadedAt: string;
  downloadURL: string;
  metadata: LoggerFirmwareMetadata;
}

@Injectable({ providedIn: 'root' })
export class FirmwareTestStorageService {
  private storage = getStorage(getApp());

  constructor(private authService: AuthenticationService) {}

  /**
   * Convert module type to path format
   * Handles BATTERY.* modules by converting dots to slashes
   */
  private getModulePath(moduleType: string): string {
    return moduleType.startsWith('BATTERY.')
      ? moduleType.replace('.', '/')
      : moduleType;
  }

  /**
   * Normalize version string for comparison
   * Removes whitespace and converts to uppercase
   */
  private normalizeVersion(version: string): string {
    return version.trim().toUpperCase();
  }

  /**
   * Upload test firmware with validation
   */
  async uploadTestFirmware(
    file: File,
    metadata: InverterFirmwareMetadata,
    onProgress?: (percent: number) => void
  ): Promise<InverterFirmwareItem> {
    // 1. Validate file extension
    if (!file.name.endsWith('.bin')) {
      throw new Error('Il file deve essere in formato .bin');
    }

    // 2. Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      throw new Error('Il file non può superare i 100MB');
    }

    // 3. Extract metadata from binary
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    let extracted: SgpMetadataFirmwareType;
    try {
      extracted = SgpInteraction.ExtractMetadataFromFirmware(uint8Array);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Firmware binario non valido: ${errorMessage}`);
    }

    // 4. Validate version match (normalized comparison)
    const normalizedMetadataVersion = this.normalizeVersion(metadata.version);
    const normalizedExtractedVersion = this.normalizeVersion(extracted.firmwareVersion);

    if (normalizedMetadataVersion !== normalizedExtractedVersion) {
      throw new Error(
        `Version mismatch: il form indica "${metadata.version}" ma il binario contiene "${extracted.firmwareVersion}"`
      );
    }

    // 5. Validate chipId match (if chipModel is present in binary) - normalized comparison
    if (extracted.chipModel && this.normalizeVersion(metadata.chipId) !== this.normalizeVersion(extracted.chipModel)) {
      throw new Error(
        `ChipId mismatch: il form indica "${metadata.chipId}" ma il binario contiene "${extracted.chipModel}"`
      );
    }

    // 6. Build Firebase Storage path
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const timestamp = Date.now();
    const filename = `${timestamp}_${metadata.version}.bin`;
    const modulePath = this.getModulePath(metadata.moduleType);
    const path = `test-firmware/${operatorId}/${metadata.deviceModel}/${modulePath}/${metadata.chipId}/${filename}`;

    // 7. Build custom metadata
    const customMetadata = {
      // From form
      deviceModel: metadata.deviceModel,
      moduleType: metadata.moduleType,
      chipId: metadata.chipId,
      version: metadata.version,
      notes: metadata.notes || '',

      // From extracted
      firmwareName: extracted.firmwareName,
      firmwareVersion: extracted.firmwareVersion,
      firmwareCRC32Hex: this.uint8ArrayToHex(extracted.firmwareCRC32),
      firmwareSizeHex: this.uint8ArrayToHex(extracted.firmwareSize),
      chipModel: extracted.chipModel || '',

      // System
      uploadedBy: operatorId,
      uploadedAt: new Date().toISOString()
    };

    // 8. Upload with progress tracking
    const storageRef = ref(this.storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: 'application/octet-stream',
      customMetadata
    });

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress?.(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const resultMetadata = await getMetadata(uploadTask.snapshot.ref);

            resolve({
              path,
              name: filename,
              size: file.size,
              uploadedAt: resultMetadata.timeCreated,
              downloadURL,
              metadata: customMetadata as any
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  /**
   * List test firmware from all operators with filters
   */
  async listTestFirmware(filters?: {
    deviceModel?: string;
    moduleType?: string;
    onlyMine?: boolean;
  }): Promise<InverterFirmwareItem[]> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const currentUserId = currentUser.uid;

    try {
      // List from root test-firmware folder to get all operators
      const rootRef = ref(this.storage, 'test-firmware');
      const rootResult = await listAll(rootRef);

      const items: InverterFirmwareItem[] = [];

      // Iterate through all operator folders (skip system folders)
      for (const operatorFolderRef of rootResult.prefixes) {
        // Skip system folders (not operator IDs)
        if (operatorFolderRef.name === 'safety' || operatorFolderRef.name === 'loggers') {
          continue;
        }

        let basePath = operatorFolderRef.fullPath;

        // Build path based on filters
        if (filters?.deviceModel) {
          basePath += `/${filters.deviceModel}`;

          if (filters.moduleType) {
            const modulePath = this.getModulePath(filters.moduleType);
            basePath += `/${modulePath}`;
          }
        }

        try {
          const listRef = ref(this.storage, basePath);
          const result = await listAll(listRef);

          // Process files at this level
          for (const itemRef of result.items) {
            try {
              const metadata = await getMetadata(itemRef);
              const downloadURL = await getDownloadURL(itemRef);

              items.push({
                path: itemRef.fullPath,
                name: itemRef.name,
                size: metadata.size,
                uploadedAt: metadata.timeCreated,
                downloadURL,
                metadata: metadata.customMetadata as any
              });
            } catch (error) {
              console.warn(`Failed to get metadata for ${itemRef.fullPath}:`, error);
            }
          }

          // Recursively process subdirectories (exclude system folders)
          for (const folderRef of result.prefixes) {
            // Skip system folders to avoid mixing different firmware types
            if (folderRef.name === 'loggers' || folderRef.name === 'safety') {
              continue;
            }
            const subItems = await this.listFromRef(folderRef);
            items.push(...subItems);
          }
        } catch (error) {
          // Skip this operator folder if there's an error (e.g., path doesn't exist with filters)
          console.warn(`Error listing firmware for operator ${operatorFolderRef.name}:`, error);
        }
      }

      // Filter by onlyMine if requested
      let filteredItems = items;
      if (filters?.onlyMine) {
        filteredItems = items.filter(item => item.metadata?.uploadedBy === currentUserId);
      }

      // Sort by uploadedAt descending
      return filteredItems.sort((a, b) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );
    } catch (error) {
      console.error('Error listing firmware:', error);
      return [];
    }
  }

  /**
   * Helper to recursively list files from a reference
   * @param ref - Storage reference to list from
   * @param depth - Current recursion depth (default: 0)
   * @param maxDepth - Maximum recursion depth (default: 10)
   */
  private async listFromRef(
    ref: StorageReference,
    depth: number = 0,
    maxDepth: number = 10
  ): Promise<InverterFirmwareItem[]> {
    // Safety check: prevent excessive recursion
    if (depth >= maxDepth) {
      console.warn(`Max recursion depth ${maxDepth} reached at ${ref.fullPath}`);
      return [];
    }

    const items: InverterFirmwareItem[] = [];
    const result = await listAll(ref);

    for (const itemRef of result.items) {
      try {
        const metadata = await getMetadata(itemRef);
        const downloadURL = await getDownloadURL(itemRef);

        items.push({
          path: itemRef.fullPath,
          name: itemRef.name,
          size: metadata.size,
          uploadedAt: metadata.timeCreated,
          downloadURL,
          metadata: metadata.customMetadata as any
        });
      } catch (error) {
        console.warn(`Failed to get metadata for ${itemRef.fullPath}:`, error);
      }
    }

    for (const folderRef of result.prefixes) {
      // Skip system folders to avoid mixing different firmware types
      if (folderRef.name === 'loggers' || folderRef.name === 'safety') {
        continue;
      }
      const subItems = await this.listFromRef(folderRef, depth + 1, maxDepth);
      items.push(...subItems);
    }

    return items;
  }

  /**
   * Download test firmware (accessible to all authenticated users)
   */
  async downloadTestFirmware(path: string): Promise<ArrayBuffer> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    // Validate path format
    if (!path.startsWith('test-firmware/')) {
      throw new Error('Path non valido');
    }

    try {
      const storageRef = ref(this.storage, path);
      const downloadURL = await getDownloadURL(storageRef);

      const response = await fetch(downloadURL);
      if (!response.ok) {
        throw new Error(`Download fallito: ${response.statusText}`);
      }

      return await response.arrayBuffer();
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

  /**
   * Validate if a path exists (for override cleanup)
   */
  async validatePath(path: string): Promise<boolean> {
    try {
      const storageRef = ref(this.storage, path);
      await getMetadata(storageRef);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Delete test firmware
   */
  async deleteTestFirmware(path: string): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    if (!path.startsWith(`test-firmware/${operatorId}/`)) {
      throw new Error('Non autorizzato: impossibile eliminare firmware di un altro operatore');
    }

    try {
      const storageRef = ref(this.storage, path);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  }

  /**
   * Convert Uint8Array to hex string
   */
  private uint8ArrayToHex(arr: Uint8Array): string {
    return Array.from(arr)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // ========== LOGGER FIRMWARE METHODS ==========

  /**
   * Upload logger firmware (NO metadata extraction)
   */
  async uploadLoggerFirmware(
    file: File,
    loggerType: LoggerType,
    metadata: { notes?: string },
    onProgress?: (percent: number) => void
  ): Promise<LoggerFirmwareItem> {
    // 1. Validate file extension
    if (!file.name.endsWith('.bin')) {
      throw new Error('Il file deve essere in formato .bin');
    }

    // 2. Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      throw new Error('Il file non può superare i 100MB');
    }

    // 3. Get current user
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const timestamp = Date.now();
    const filename = `${timestamp}_${file.name}`;

    // 4. Build Firebase Storage path (NO chipId/deviceModel, only loggerType)
    const path = `test-firmware/${operatorId}/loggers/${loggerType}/${filename}`;

    // 5. Build custom metadata
    const customMetadata: LoggerFirmwareMetadata = {
      deviceType: 'LOGGER',
      loggerType,
      originalFilename: file.name,
      uploadedBy: operatorId,
      uploadedAt: new Date().toISOString(),
      notes: metadata.notes || ''
    };

    // 6. Upload with progress tracking
    const storageRef = ref(this.storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: 'application/octet-stream',
      customMetadata: customMetadata as any
    });

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress?.(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const resultMetadata = await getMetadata(uploadTask.snapshot.ref);

            resolve({
              path,
              name: filename,
              size: file.size,
              uploadedAt: resultMetadata.timeCreated,
              downloadURL,
              metadata: customMetadata
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  /**
   * List logger firmware from all operators with optional filter
   */
  async listLoggerFirmware(filters?: {
    loggerType?: LoggerType;
    onlyMine?: boolean;
  }): Promise<LoggerFirmwareItem[]> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const currentUserId = currentUser.uid;

    try {
      // List from root test-firmware folder to get all operators
      const rootRef = ref(this.storage, 'test-firmware');
      const rootResult = await listAll(rootRef);

      const items: LoggerFirmwareItem[] = [];

      // Iterate through all operator folders
      for (const operatorFolderRef of rootResult.prefixes) {
        let basePath = `${operatorFolderRef.fullPath}/loggers`;

        if (filters?.loggerType) {
          basePath += `/${filters.loggerType}`;
        }

        try {
          const listRef = ref(this.storage, basePath);
          const result = await listAll(listRef);

          // Process files at this level
          for (const itemRef of result.items) {
            try {
              const metadata = await getMetadata(itemRef);
              const downloadURL = await getDownloadURL(itemRef);

              items.push({
                path: itemRef.fullPath,
                name: itemRef.name,
                size: metadata.size,
                uploadedAt: metadata.timeCreated,
                downloadURL,
                metadata: metadata.customMetadata as unknown as LoggerFirmwareMetadata
              });
            } catch (error) {
              console.warn(`Failed to get metadata for ${itemRef.fullPath}:`, error);
            }
          }

          // Recursively process subdirectories (for loggerType filter)
          for (const folderRef of result.prefixes) {
            const subItems = await this.listLoggerFromRef(folderRef);
            items.push(...subItems);
          }
        } catch (error) {
          // Skip this operator folder if there's an error (e.g., no loggers folder)
          console.warn(`Error listing logger firmware for operator ${operatorFolderRef.name}:`, error);
        }
      }

      // Filter by onlyMine if requested
      let filteredItems = items;
      if (filters?.onlyMine) {
        filteredItems = items.filter(item => item.metadata?.uploadedBy === currentUserId);
      }

      // Sort by uploadedAt descending
      return filteredItems.sort((a, b) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );
    } catch (error) {
      console.error('Error listing logger firmware:', error);
      return [];
    }
  }

  /**
   * Helper to recursively list logger files from a reference
   * @param ref - Storage reference to list from
   * @param depth - Current recursion depth (default: 0)
   * @param maxDepth - Maximum recursion depth (default: 10)
   */
  private async listLoggerFromRef(
    ref: StorageReference,
    depth: number = 0,
    maxDepth: number = 10
  ): Promise<LoggerFirmwareItem[]> {
    // Safety check: prevent excessive recursion
    if (depth >= maxDepth) {
      console.warn(`Max recursion depth ${maxDepth} reached at ${ref.fullPath}`);
      return [];
    }

    const items: LoggerFirmwareItem[] = [];
    const result = await listAll(ref);

    for (const itemRef of result.items) {
      try {
        const metadata = await getMetadata(itemRef);
        const downloadURL = await getDownloadURL(itemRef);

        items.push({
          path: itemRef.fullPath,
          name: itemRef.name,
          size: metadata.size,
          uploadedAt: metadata.timeCreated,
          downloadURL,
          metadata: metadata.customMetadata as unknown as LoggerFirmwareMetadata
        });
      } catch (error) {
        console.warn(`Failed to get metadata for ${itemRef.fullPath}:`, error);
      }
    }

    for (const folderRef of result.prefixes) {
      const subItems = await this.listLoggerFromRef(folderRef, depth + 1, maxDepth);
      items.push(...subItems);
    }

    return items;
  }

  /**
   * Delete logger firmware
   */
  async deleteLoggerFirmware(path: string): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    if (!path.startsWith(`test-firmware/${operatorId}/loggers/`)) {
      throw new Error('Non autorizzato: impossibile eliminare firmware di un altro operatore');
    }

    try {
      const storageRef = ref(this.storage, path);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  }


  /**
   * Upload SAFETY binary with buildDate extraction
   * Files are SHARED (no operatorId in path)
   */
  async uploadSafetyBinary(
    file: File,
    metadata: { deviceModel: string; notes?: string },
    onProgress?: (percent: number) => void
  ): Promise<SafetyBinaryItem> {
    // 1. Validate file extension
    if (!file.name.endsWith('.bin')) {
      throw new Error('Il file deve essere in formato .bin');
    }

    // 2. Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      throw new Error('Il file non può superare i 100MB');
    }

    // 3. Get current user for metadata
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    // 4. Extract metadata from binary using V1 format (for buildDate)
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    let extracted: SgpMetadataFirmwareType;
    try {
      extracted = SgpInteraction.ExtractMetadataFromFirmware(uint8Array);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Safety binary non valido: ${errorMessage}`);
    }

    if (!extracted.buildDate) {
      throw new Error('buildDate non trovato nel binario');
    }

    // 5. Build Firebase Storage path (SHARED - no operatorId)
    // Format: test-firmware/safety/binaries/{deviceModel}/{deviceModel}_SAFETY_{buildDate}.bin
    const sanitizedBuildDate = extracted.buildDate.replace(/[^a-zA-Z0-9]/g, '');
    const filename = `${metadata.deviceModel}_SAFETY_${sanitizedBuildDate}.bin`;
    const path = `test-firmware/safety/binaries/${metadata.deviceModel}/${filename}`;

    // 6. Build custom metadata
    const customMetadata: SafetyBinaryMetadata = {
      deviceModel: metadata.deviceModel,
      buildDate: extracted.buildDate,
      firmwareName: extracted.firmwareName,
      firmwareVersion: extracted.firmwareVersion,
      uploadedBy: currentUser.uid,
      uploadedAt: new Date().toISOString(),
      notes: metadata.notes || ''
    };

    // 7. Upload with progress tracking
    const storageRef = ref(this.storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: 'application/octet-stream',
      customMetadata: customMetadata as any
    });

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress?.(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const resultMetadata = await getMetadata(uploadTask.snapshot.ref);

            resolve({
              path,
              name: filename,
              size: file.size,
              uploadedAt: resultMetadata.timeCreated,
              downloadURL,
              metadata: customMetadata
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  /**
   * Upload SAFETY custom .txt file
   * Parses first line to extract country/region/version for naming
   * Files are SHARED (no operatorId in path)
   */
  async uploadSafetyCustom(
    file: File,
    metadata: { deviceModel: string; notes?: string },
    onProgress?: (percent: number) => void
  ): Promise<SafetyCustomItem> {
    // 1. Validate file extension
    if (!file.name.endsWith('.txt')) {
      throw new Error('Il file deve essere in formato .txt');
    }

    // 2. Validate file size (max 10MB for txt)
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('Il file non può superare i 10MB');
    }

    // 3. Get current user for metadata
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    // 4. Read file content and parse header
    const content = await file.text();
    const parsed = parseSafetyCustomHeader(content);

    if (!parsed) {
      throw new Error('Header non valido. Formato atteso: COUNTRY&Region&Ver:{countryCode},{regionCode},{version}');
    }

    // 5. Build Firebase Storage path (SHARED - no operatorId)
    // Format: test-firmware/safety/custom/{deviceModel}/{countryCode}_{regionCode}_{version}.txt
    const overrideKey = buildSafetyOverrideKey(parsed);
    const filename = `${overrideKey}.txt`;
    const path = `test-firmware/safety/custom/${metadata.deviceModel}/${filename}`;

    // 6. Build custom metadata
    const customMetadata: SafetyCustomMetadata = {
      deviceModel: metadata.deviceModel,
      countryCode: parsed.countryCode,
      regionCode: parsed.regionCode,
      version: parsed.version,
      countryName: getCountryNameFromCodeV3((parsed.countryCode << 8) | parsed.regionCode),
      overrideKey,
      uploadedBy: currentUser.uid,
      uploadedAt: new Date().toISOString(),
      notes: metadata.notes || ''
    };

    // 7. Upload with progress tracking
    const storageRef = ref(this.storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: 'text/plain',
      customMetadata: customMetadata as any
    });

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress?.(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const resultMetadata = await getMetadata(uploadTask.snapshot.ref);

            resolve({
              path,
              name: filename,
              size: file.size,
              uploadedAt: resultMetadata.timeCreated,
              downloadURL,
              metadata: customMetadata
            });
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  }

  /**
   * List SAFETY binaries (shared across all operators)
   */
  async listSafetyBinaries(filters?: {
    deviceModel?: string;
  }): Promise<SafetyBinaryItem[]> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    try {
      let basePath = 'test-firmware/safety/binaries';
      if (filters?.deviceModel) {
        basePath += `/${filters.deviceModel}`;
      }

      const listRef = ref(this.storage, basePath);
      const items = await this.listSafetyFromRef<SafetyBinaryItem, SafetyBinaryMetadata>(listRef);

      // Sort by uploadedAt descending
      return items.sort((a, b) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );
    } catch (error) {
      console.error('Error listing safety binaries:', error);
      return [];
    }
  }

  /**
   * List SAFETY custom .txt files (shared across all operators)
   */
  async listSafetyCustom(filters?: {
    deviceModel?: string;
  }): Promise<SafetyCustomItem[]> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    try {
      let basePath = 'test-firmware/safety/custom';
      if (filters?.deviceModel) {
        basePath += `/${filters.deviceModel}`;
      }

      const listRef = ref(this.storage, basePath);
      const items = await this.listSafetyFromRef<SafetyCustomItem, SafetyCustomMetadata>(listRef);

      // Sort by uploadedAt descending
      return items.sort((a, b) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
      );
    } catch (error) {
      console.error('Error listing safety custom files:', error);
      return [];
    }
  }

  /**
   * Helper to recursively list safety files from a reference
   * @param storageRef - Storage reference to list from
   * @param depth - Current recursion depth (default: 0)
   * @param maxDepth - Maximum recursion depth (default: 10)
   */
  private async listSafetyFromRef<T extends { path: string; name: string; size: number; uploadedAt: string; downloadURL: string; metadata: M }, M>(
    storageRef: StorageReference,
    depth: number = 0,
    maxDepth: number = 10
  ): Promise<T[]> {
    // Safety check: prevent excessive recursion
    if (depth >= maxDepth) {
      console.warn(`Max recursion depth ${maxDepth} reached at ${storageRef.fullPath}`);
      return [];
    }

    const items: T[] = [];
    const result = await listAll(storageRef);

    for (const itemRef of result.items) {
      try {
        const metadata = await getMetadata(itemRef);
        const downloadURL = await getDownloadURL(itemRef);

        items.push({
          path: itemRef.fullPath,
          name: itemRef.name,
          size: metadata.size,
          uploadedAt: metadata.timeCreated,
          downloadURL,
          metadata: metadata.customMetadata as unknown as M
        } as T);
      } catch (error) {
        console.warn(`Failed to get metadata for ${itemRef.fullPath}:`, error);
      }
    }

    for (const folderRef of result.prefixes) {
      const subItems = await this.listSafetyFromRef<T, M>(folderRef, depth + 1, maxDepth);
      items.push(...subItems);
    }

    return items;
  }
}
